/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2023
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { useEffect, useState } from 'react';
import _ from 'lodash';
import { IMessageDescriptor } from '@scuf/localization/dist/types';
import descriptors from '../../../../../locales/en-US/descriptors';

export type IBytes = number;
export interface IXLSXFileUploadSettings {
  extensions: string[];
  minSize: IBytes;
  maxSize: IBytes;
  mimeTypes: string[];
  downloadFile?: string; // for template download
  uploadType?: string; // for template download
  // [k: string]: IAny;
}

export interface IValidationMessageDescriptor {
  d: IMessageDescriptor;
  args?: object;
  type?: 'error' | 'info' | 'child';
}

export interface IValidationResponse {
  success: boolean;
  messages: IValidationMessageDescriptor[];
}

const _sizeDecimal = 2;

export interface IFileValidatorHook {
  isFileValid: boolean;
  validationMessages: IValidationMessageDescriptor[];
  validateFile: (file: File) => void;
  clearFileValidator: () => void;
}

/**
 * React hook to check file is valid wrt size, type, mime
 * @param allowedFileSettings IXLSXFileUploadSettings
 * @returns IFileValidatorHook
 */
export const useFileValidator = (
  allowedFileSettings: IXLSXFileUploadSettings
): IFileValidatorHook => {
  const [isFileValid, setIsFileValid] = useState<boolean>(false);
  const [hasFile, setHasFile] = useState<boolean>(false);
  const [validationMessages, setValidationMessages] = useState<
    IValidationMessageDescriptor[]
  >([]);

  const _updateValMessages = (newM: IValidationMessageDescriptor) => {
    setValidationMessages((prevM) => [...prevM, newM]);
  };

  useEffect(() => {
    setIsFileValid(hasFile && validationMessages.length === 0);
  }, [validationMessages, hasFile]);

  const validateFile = (file: File) => {
    setValidationMessages([]);
    try {
      const fNameArr = file.name.split('.');
      const fExt = fNameArr[fNameArr.length - 1];
      const oneKb = 1024;

      if (!_.includes(allowedFileSettings.mimeTypes, file.type)) {
        _updateValMessages({
          d: descriptors.equipmentSpaceMapping.INVALIDFILE_MIME,
          args: { mimeTypes: allowedFileSettings.mimeTypes }
        });
      }
      if (!_.includes(allowedFileSettings.extensions, fExt)) {
        _updateValMessages({
          d: descriptors.equipmentSpaceMapping.INVALIDFILE_EXTENSION,
          args: { extension: _.join(allowedFileSettings.extensions, ', ') }
        });
      }
      if (file.size > allowedFileSettings.maxSize) {
        _updateValMessages({
          d: descriptors.equipmentSpaceMapping.INVALIDFILE_MAXALLOWEDSIZE,
          args: {
            size: `${_.toInteger(
              allowedFileSettings.maxSize / (oneKb * oneKb)
            )} MB`
          }
        });
      }
      if (file.size < allowedFileSettings.minSize) {
        _updateValMessages({
          d: descriptors.equipmentSpaceMapping.INVALIDFILE_MINALLOWEDSIZE,
          args: { size: `${allowedFileSettings.minSize} bytes` }
        });
      }
    } catch (e) {
      _updateValMessages({
        d: descriptors.equipmentSpaceMapping.INVALIDFILE_UNKNOWNERROR
      });
    }
    setHasFile(true);
  };

  const clearFileValidator = () => {
    setIsFileValid(false);
    setHasFile(false);
    setValidationMessages([]);
  };

  return { isFileValid, validationMessages, validateFile, clearFileValidator };
};
